import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Lightweight local typing to avoid mismatch with @types/keycloak-js
type KeycloakLike = any;

export type AuthContextValue = {
  initialized: boolean;
  authenticated: boolean;
  loading: boolean;
  token?: string | null;
  login: (opts?: Record<string, unknown>) => Promise<void>;
  logout: (opts?: Record<string, unknown>) => Promise<void>;
  getToken: () => Promise<string | undefined | null>;
};

const AuthContext = createContext<AuthContextValue | undefined>(
  undefined
);

// Patch global fetch to attach Authorization header when available and ensure token refresh
function installFetchInterceptor(
  getToken: () => Promise<string | undefined | null>
) {
  if (typeof window === 'undefined' || !(window as any).fetch) return;
  const originalFetch = window.fetch.bind(window);

  // Avoid double-wrapping
  if ((window as any).__authFetchInstalled) return;
  (window as any).__authFetchInstalled = true;

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    try {
      const token = await getToken();
      const headers = new Headers(
        (init && init.headers) || undefined
      );

      if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      const nextInit: RequestInit = {
        ...(init || {}),
        headers,
      };

      return originalFetch(input as any, nextInit);
    } catch (e) {
      // If token retrieval fails, proceed without auth to avoid breaking non-protected calls
      return originalFetch(input as any, init as any);
    }
  };
}

export const KeycloakProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const keycloakRef = useRef<KeycloakLike | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const getToken = useCallback(async () => {
    const kc = keycloakRef.current;
    if (!kc) return undefined;
    // Try to refresh if expiring within 30s
    try {
      if (kc.token) {
        await kc.updateToken(30);
      }
    } catch (_) {
      // ignore; consumer may trigger login
    }
    return kc.token as string | undefined | null;
  }, []);

  const login = useCallback(
    async (opts?: Record<string, unknown>) => {
      const kc = keycloakRef.current;
      if (!kc) return;
      await kc.login({
        redirectUri: window.location.href,
        ...opts,
      } as any);
    },
    []
  );

  const logout = useCallback(
    async (opts?: Record<string, unknown>) => {
      const kc = keycloakRef.current;
      if (!kc) return;
      await kc.logout({
        redirectUri: window.location.origin,
        ...opts,
      } as any);
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { default: Keycloak } = await import(
          /* webpackChunkName: "keycloak-js" */ 'keycloak-js'
        );

        const kc: KeycloakLike = new (Keycloak as any)({
          url:
            (window as any).__ENV__?.KEYCLOAK_URL ||
            (window as any).__ENV__?.REACT_APP_AUTH_URL ||
            '',
          realm: (window as any).__ENV__?.KEYCLOAK_REALM || 'master',
          clientId:
            (window as any).__ENV__?.KEYCLOAK_CLIENT_ID ||
            'report-form',
        });
        keycloakRef.current = kc;

        kc.onAuthSuccess = () => {
          if (cancelled) return;
          setAuthenticated(true);
          setToken(kc.token || null);
        };
        kc.onAuthError = () => {
          if (cancelled) return;
          setAuthenticated(false);
        };
        kc.onAuthLogout = () => {
          if (cancelled) return;
          setAuthenticated(false);
          setToken(null);
        };
        kc.onTokenExpired = async () => {
          try {
            await kc.updateToken(30);
            setToken(kc.token || null);
          } catch (_) {
            // If refresh fails, stay unauthenticated until next login
            setAuthenticated(false);
            setToken(null);
          }
        };

        const silentCheckSsoRedirectUri = `${window.location.origin}/silent-check-sso.html`;

        await kc.init({
          onLoad: 'check-sso',
          pkceMethod: 'S256',
          checkLoginIframe: true,
          silentCheckSsoRedirectUri,
          enableLogging: false,
        } as any);

        if (!cancelled) {
          setInitialized(true);
          setAuthenticated(!!kc.authenticated);
          setToken(kc.token || null);
          installFetchInterceptor(getToken);
        }
      } catch (e) {
        if (!cancelled) {
          // Initialization failed; app can still run unauthenticated
          setInitialized(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const value = useMemo<AuthContextValue>(
    () => ({
      initialized,
      authenticated,
      loading,
      token,
      login,
      logout,
      getToken,
    }),
    [
      initialized,
      authenticated,
      loading,
      token,
      login,
      logout,
      getToken,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx)
    throw new Error('useAuth must be used within KeycloakProvider');
  return ctx;
}
